import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

/**
 * Translates dynamic content from DB using the AI translation edge function.
 * - French returns identity (source language).
 * - Deduplicates texts and uses a stable, small query key.
 * - Persists translations in localStorage so repeat visits render instantly
 *   in the target language before the network response arrives.
 * - Long staleTime because translations are content-hash keyed and immutable.
 */

const LS_PREFIX = "lpc:i18n:";
const LS_MAX_ENTRIES = 500;

function lsKey(lang: string) {
  return `${LS_PREFIX}${lang}`;
}

function readLS(lang: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(lsKey(lang));
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

function writeLS(lang: string, map: Record<string, string>) {
  try {
    const entries = Object.entries(map);
    // Bound size: keep the most recent LS_MAX_ENTRIES.
    const bounded =
      entries.length > LS_MAX_ENTRIES
        ? Object.fromEntries(entries.slice(-LS_MAX_ENTRIES))
        : map;
    localStorage.setItem(lsKey(lang), JSON.stringify(bounded));
  } catch {
    /* quota exceeded / SSR — ignore */
  }
}

// Small fast hash — used to build a stable query key without listing every
// string in it. Avoids react-query re-fetching when only the input order
// changes, and keeps devtools readable.
function cheapHash(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function useTranslatedContent(texts: (string | null | undefined)[]) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isFr = lang === "fr" || lang.startsWith("fr-");

  // Dedupe + sort so the query key is stable regardless of caller order.
  const uniqueSorted = useMemo(() => {
    const s = new Set<string>();
    for (const t of texts) {
      if (t && t.trim().length > 0) s.add(t);
    }
    return Array.from(s).sort();
  }, [texts]);

  const inputSignature = useMemo(
    () => cheapHash(uniqueSorted.join("\u0001")),
    [uniqueSorted]
  );

  // Seed with anything we already know from previous sessions.
  const seed = useMemo<Record<string, string>>(() => {
    if (isFr || uniqueSorted.length === 0) return {};
    const stored = readLS(lang);
    const out: Record<string, string> = {};
    for (const text of uniqueSorted) {
      const v = stored[text];
      if (v !== undefined) out[text] = v;
    }
    return out;
  }, [isFr, lang, uniqueSorted]);

  // If every requested text is already cached locally, skip the network call.
  const allCached =
    !isFr &&
    uniqueSorted.length > 0 &&
    uniqueSorted.every((t) => seed[t] !== undefined);

  const { data: translations } = useQuery({
    queryKey: ["translations", lang, inputSignature],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("translate", {
        body: { texts: uniqueSorted, targetLang: lang },
      });
      if (error) throw error;
      const fresh = (data?.translations ?? {}) as Record<string, string>;
      // Merge into localStorage cache for next session.
      const merged = { ...readLS(lang), ...fresh };
      writeLS(lang, merged);
      return { ...seed, ...fresh };
    },
    enabled: !isFr && uniqueSorted.length > 0 && !allCached,
    initialData: allCached ? seed : undefined,
    staleTime: Infinity, // content-hash keyed on the server → immutable
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const active = translations ?? seed;

  const t = (text: string | null | undefined): string => {
    if (!text) return "";
    if (isFr) return text;
    return active?.[text] || text;
  };

  return { t, lang, isFr };
}
